/**
 * JourneyPublic — public read-only page a student can share.
 *
 *   /journey/:ticketId  →  hero ticket + a short "how this works"
 *                          block and a CTA to build-your-own.
 *
 * No auth, no editable surfaces. Shows a 404 when the ticket doesn't
 * exist so bad links don't leak that an account was once there.
 */
import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, API } from "@/lib/api";
import MetroTicket from "@/components/app/MetroTicket";
import { Helmet } from "react-helmet-async";
import { ArrowRight, Share2, Compass } from "lucide-react";

export default function JourneyPublic() {
  const { ticketId } = useParams();
  const [data, setData] = useState(null);
  const [state, setState] = useState("loading"); // loading | ok | notfound
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!ticketId) return;
    api.get(`/ticket/${ticketId}`)
      .then((r) => { setData(r.data); setState("ok"); })
      .catch(() => setState("notfound"));
  }, [ticketId]);

  const absUrl = `${window.location.origin}/journey/${ticketId}`;
  const ogImg = `${API}/ticket/share.png?tid=${ticketId}`;

  if (state === "loading") {
    return (
      <div className="max-w-3xl mx-auto px-6 py-16 text-stone-500 text-sm" data-testid="journey-public-loading">
        Loading journey ticket…
      </div>
    );
  }
  if (state === "notfound") {
    return (
      <div className="max-w-3xl mx-auto px-6 py-16" data-testid="journey-public-notfound">
        <h1 className="text-2xl font-semibold text-stone-900">Ticket not found</h1>
        <p className="mt-2 text-sm text-stone-600">
          This journey link looks expired or isn't published. If it's
          yours, head to <Link to="/profile" className="text-[#4C1D95] underline">your profile</Link>{" "}
          to get a fresh share link.
        </p>
      </div>
    );
  }

  const headline = `${data.name}'s journey — ${data.destination || "destination to be revealed"}`;
  const description = [
    data.origin && `From ${data.origin}`,
    data.stopping_at && `stopping at ${data.stopping_at}`,
    data.carriage && `studying ${data.carriage}`,
  ].filter(Boolean).join(" · ") || "Build your own university journey ticket.";

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(absUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    } catch {
      window.prompt("Copy this link:", absUrl);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#F5F3FF] via-white to-white">
      <Helmet>
        <title>{headline}</title>
        <meta name="description" content={description} />
        <meta property="og:title" content={headline} />
        <meta property="og:description" content={description} />
        <meta property="og:image" content={ogImg} />
        <meta property="og:url" content={absUrl} />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={headline} />
        <meta name="twitter:description" content={description} />
        <meta name="twitter:image" content={ogImg} />
      </Helmet>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-10 sm:py-16">
        <div className="text-center mb-6">
          <div className="inline-flex items-center gap-2 text-[11px] font-semibold tracking-widest text-[#5B21B6] uppercase">
            <Compass className="h-3.5 w-3.5" />
            Which University Advisor · Journey
          </div>
          <h1 className="mt-3 text-3xl sm:text-4xl font-black text-stone-900 leading-tight">
            {data.name}'s ticket to where they're meant to be
          </h1>
          {data.destination && (
            <p className="mt-2 text-base text-stone-600">
              Destination: <span className="font-semibold text-[#4C1D95]">{data.destination}</span>
            </p>
          )}
        </div>

        <div className="flex justify-center">
          <div className="w-full max-w-[540px]">
            <MetroTicket data={data} vertical={false} publicUrl={`/journey/${ticketId}`} />
          </div>
        </div>

        <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
          <button
            type="button"
            onClick={copyLink}
            data-testid="journey-public-copy"
            className="inline-flex items-center gap-2 bg-[#4C1D95] hover:bg-[#5B21B6] text-white text-sm font-semibold px-4 py-2.5 rounded-md transition-colors"
          >
            <Share2 className="h-4 w-4" />
            {copied ? "Link copied" : "Copy share link"}
          </button>
          <Link
            to="/profile"
            className="inline-flex items-center gap-1.5 border border-stone-800 text-stone-900 hover:bg-stone-50 text-sm font-semibold px-4 py-2.5 rounded-md transition-colors"
            data-testid="journey-public-build-own"
          >
            Build your own ticket
            <ArrowRight className="h-4 w-4" />
          </Link>
        </div>

        <div className="mt-12 max-w-xl mx-auto text-center text-[13px] text-stone-500 leading-relaxed">
          Every student's journey is personal. We'll help you figure out which university
          is best for <span className="italic text-stone-700">you</span> — using graduate
          salaries and real employer pipelines, not rankings.
        </div>
      </div>
    </div>
  );
}
